/**
 * Enum-to-pill mappings, split out of StatusPill.tsx.
 *
 * They lived alongside the component, which meant that file exported thirteen
 * non-components and tripped react-refresh/only-export-components thirteen
 * times — every edit to any of these invalidated the component's fast-refresh
 * boundary. No file imported both halves, so the split was a pure move.
 *
 * It also outlives the component: common/js/web-ui exports its own StatusPill, and
 * when this app adopts it these mappings stay exactly as they are.
 */

import { PARTNER_CATEGORY_LABELS } from '@liminal/content/rewards';
import type { RampProvider, PersonaInquiryState, RewardMerchant_Category } from '@/api/generated';
import {
  Provider,
  InquiryState,
  FeedbackKind,
  OnboardingGoal,
  AcquisitionSource,
  RewardMerchant_State,
  RewardBudget_State,
  RewardPayment_State,
  RewardRedemption_State,
  NimCall_State,
  NimCallInitiator,
  NimCallEndReason,
  ConversationFlag_State,
  ConversationFlag_Source,
  EventGuest_Eligibility,
  AttributionSource,
} from '@/api/generated';

/**
 * admin_api's kyc and users domains each carry their own copy of the
 * Persona inquiry state (CP9: one Go package backs the docs pool, so two
 * domains cannot declare one name). The values are identical, so the maps
 * below are keyed by value and accept either copy.
 */
type AnyInquiryState = InquiryState | PersonaInquiryState;
/** Likewise the ramp provider, mirrored into admin's kyc domain. */
type AnyProvider = Provider | RampProvider;
import type { StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import type { MerchantAvailability } from '@/lib/rewards';

// -- KYC --

// The admin API sends KYC_STATUS_UNSPECIFIED for a user with no Persona inquiry
// yet. The generated KYCState enum drops proto zero values, so it lives here.
export const KYC_STATUS_NOT_STARTED = 'KYC_STATUS_UNSPECIFIED';

export function kycStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('VERIFIED')) return 'success';
  if (status.includes('REJECTED') || status.includes('SUSPENDED')) return 'error';
  if (status.includes('PENDING') || status.includes('SUBMITTED') || status.includes('REVIEW') || status.includes('NEEDS_INFO')) return 'warning';
  return 'neutral';
}

export function kycStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  if (status === KYC_STATUS_NOT_STARTED) return 'Not started';
  return status.replace('KYC_STATUS_', '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

// -- Persona inquiry status --

const PERSONA_STATUS_LABELS: Record<AnyInquiryState, string> = {
  [InquiryState.INQUIRY_STATUS_CREATED]: 'Created',
  [InquiryState.INQUIRY_STATUS_PENDING]: 'Pending',
  [InquiryState.INQUIRY_STATUS_COMPLETED]: 'Completed',
  [InquiryState.INQUIRY_STATUS_EXPIRED]: 'Expired',
  [InquiryState.INQUIRY_STATUS_FAILED]: 'Failed',
  [InquiryState.INQUIRY_STATUS_NEEDS_REVIEW]: 'Needs Review',
  [InquiryState.INQUIRY_STATUS_APPROVED]: 'Approved',
  [InquiryState.INQUIRY_STATUS_DECLINED]: 'Declined',
};

const PERSONA_STATUS_VARIANTS: Record<AnyInquiryState, StatusPillTone> = {
  [InquiryState.INQUIRY_STATUS_CREATED]: 'neutral',
  [InquiryState.INQUIRY_STATUS_PENDING]: 'warning',
  [InquiryState.INQUIRY_STATUS_COMPLETED]: 'warning',
  [InquiryState.INQUIRY_STATUS_EXPIRED]: 'neutral',
  [InquiryState.INQUIRY_STATUS_FAILED]: 'error',
  [InquiryState.INQUIRY_STATUS_NEEDS_REVIEW]: 'warning',
  [InquiryState.INQUIRY_STATUS_APPROVED]: 'success',
  [InquiryState.INQUIRY_STATUS_DECLINED]: 'error',
};

export function personaStatusLabel(status: AnyInquiryState | undefined): string {
  if (!status) return 'Not Started';
  return PERSONA_STATUS_LABELS[status] ?? 'Unknown';
}

export function personaStatusVariant(status: AnyInquiryState | undefined): StatusPillTone {
  if (!status) return 'neutral';
  return PERSONA_STATUS_VARIANTS[status] ?? 'neutral';
}

// -- Ramp providers --

const PROVIDER_LABELS: Record<AnyProvider, string> = {
  [Provider.PROVIDER_FERN]: 'Fern',
  [Provider.PROVIDER_AVENIA]: 'Avenia',
  [Provider.PROVIDER_RAIN]: 'Rain',
  [Provider.PROVIDER_BRIDGE]: 'Bridge',
  [Provider.PROVIDER_MANTECA]: 'Manteca',
  [Provider.PROVIDER_BLINDPAY]: 'BlindPay',
};

// Ordered list of all known ramp providers — used for column headers and
// fixed-position renders so columns don't reorder per row.
export const ALL_RAMP_PROVIDERS: AnyProvider[] = [
  Provider.PROVIDER_FERN,
  Provider.PROVIDER_AVENIA,
  Provider.PROVIDER_RAIN,
  Provider.PROVIDER_BRIDGE,
  Provider.PROVIDER_MANTECA,
  Provider.PROVIDER_BLINDPAY,
];

export function providerLabel(p: AnyProvider | undefined): string {
  if (!p) return 'Unknown';
  return PROVIDER_LABELS[p] ?? 'Unknown';
}

// -- Transactions --

/**
 * Money in, money out — so `gain`/`loss`, not `success`/`warning`.
 *
 * These used to return status tones. That rendered correctly only by accident:
 * `state.success` and `semantic.gain` happen to share a hex today, so a deposit
 * came out green either way. The moment they diverge — and they were different
 * colours until LIM-2474 — every deposit and withdrawal row in the app would
 * have re-coloured to whatever "succeeded" happens to look like.
 *
 * `tokens/semantic.ts` is explicit that gain/loss are for money and must never
 * be reused for status. The inverse holds too, which is what this is.
 */
export function txContextVariant(context?: string): StatusPillTone {
  if (!context) return 'neutral';
  if (context.includes('RECEIVE')) return 'gain';
  if (context.includes('PAYMENT') || context.includes('BOLETO')) return 'loss';
  if (context.includes('DEPOSIT') || context.includes('ONRAMP') || context.includes('BUY')) return 'gain';
  if (context.includes('WITHDRAW') || context.includes('OFFRAMP') || context.includes('SELL')) return 'loss';
  if (context.includes('P2P')) return 'info';
  return 'neutral';
}

export function txStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('CONFIRMED')) return 'success';
  if (status.includes('PENDING')) return 'warning';
  if (status.includes('FAILED')) return 'error';
  return 'neutral';
}

// -- Wallet (on-chain) transactions --

/**
 * `wallets.transactions.status` is a free-text column, not a proto enum: the
 * service writes the lowercase words below (wallets/db/model/model.go). They
 * share no prefix or casing with the ledger's TRANSACTION_STATUS_* enum, so
 * txStatusVariant/formatEnumLabel would fall through to neutral on every row.
 */
const WALLET_TX_STATUS_TONES: Record<string, StatusPillTone> = {
  pending: 'warning',
  success: 'success',
  failed: 'error',
};

export function walletTxStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  return WALLET_TX_STATUS_TONES[status.toLowerCase()] ?? 'neutral';
}

export function walletTxStatusLabel(status?: string): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/** A ledger direction is money, not status. See txContextVariant above. */
export function directionVariant(direction?: string): StatusPillTone {
  if (!direction) return 'neutral';
  if (direction.includes('CREDIT')) return 'gain';
  if (direction.includes('DEBIT')) return 'loss';
  return 'neutral';
}

export function formatEnumLabel(value?: string): string {
  if (!value) return '—';
  return value
    .replace(/^TRANSACTION_CONTEXT_|^TRANSACTION_STATUS_|^DIRECTION_|^KYC_STATUS_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

// -- Announcements --

export function announcementTypeVariant(type?: string): StatusPillTone {
  if (!type) return 'neutral';
  if (type.includes('URGENT')) return 'error';
  if (type.includes('FEATURE')) return 'info';
  if (type.includes('PROMO')) return 'success';
  return 'neutral'; // MAINTENANCE
}

export function announcementTypeLabel(type?: string): string {
  if (!type) return 'Unknown';
  return type
    .replace('ANNOUNCEMENT_TYPE_', '')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

export function announcementStatusFromDates(startsAt?: string, expiresAt?: string): { label: string; variant: StatusPillTone } {
  const now = new Date();
  if (startsAt && new Date(startsAt) > now) return { label: 'Scheduled', variant: 'warning' };
  if (expiresAt && new Date(expiresAt) <= now) return { label: 'Expired', variant: 'neutral' };
  return { label: 'Active', variant: 'success' };
}

/**
 * A Nim announcement's state, which is derived rather than stored.
 *
 * An all-users announcement has no snapshot to complete against — it keeps
 * delivering to new signups — so it is never "Delivered", only Active until
 * someone cancels it.
 */
export function nimAnnouncementStatus(announcement: {
  cancelledAt?: string;
  allUsers?: boolean;
  deliveredCount?: number;
  recipientCount?: number;
}): { label: string; variant: StatusPillTone } {
  if (announcement.cancelledAt) return { label: 'Cancelled', variant: 'error' };
  if (announcement.allUsers) return { label: 'Active', variant: 'info' };
  if ((announcement.deliveredCount ?? 0) >= (announcement.recipientCount ?? 0)) {
    return { label: 'Delivered', variant: 'success' };
  }
  return { label: 'Delivering', variant: 'info' };
}

// -- Push announcements --

export function pushAnnouncementStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('SCHEDULED')) return 'info';
  if (status.includes('SENDING')) return 'warning';
  if (status.includes('SENT')) return 'success';
  return 'neutral'; // CANCELLED
}

export function pushAnnouncementStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  return status
    .replace('PUSH_ANNOUNCEMENT_STATUS_', '')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

// -- Nim feedback --

const FEEDBACK_KIND_LABELS: Record<FeedbackKind, string> = {
  [FeedbackKind.FEEDBACK_KIND_BUG]: 'Bug',
  [FeedbackKind.FEEDBACK_KIND_FEATURE_REQUEST]: 'Feature request',
  [FeedbackKind.FEEDBACK_KIND_FEEDBACK]: 'Feedback',
};

/**
 * A bug is the only category that means something is broken, so it is the only
 * one in an alarming tone. The other two are things to read, not to fix.
 */
const FEEDBACK_KIND_VARIANTS: Record<FeedbackKind, StatusPillTone> = {
  [FeedbackKind.FEEDBACK_KIND_BUG]: 'error',
  [FeedbackKind.FEEDBACK_KIND_FEATURE_REQUEST]: 'info',
  [FeedbackKind.FEEDBACK_KIND_FEEDBACK]: 'neutral',
};

export function feedbackKindLabel(kind: FeedbackKind | undefined): string {
  if (!kind) return 'Unknown';
  return FEEDBACK_KIND_LABELS[kind] ?? 'Unknown';
}

export function feedbackKindVariant(kind: FeedbackKind | undefined): StatusPillTone {
  if (!kind) return 'neutral';
  return FEEDBACK_KIND_VARIANTS[kind] ?? 'neutral';
}

// -- Onboarding goal (LIM-3545) --

const ONBOARDING_GOAL_LABELS: Record<OnboardingGoal, string> = {
  [OnboardingGoal.ONBOARDING_GOAL_INVESTING]: 'Investing',
  [OnboardingGoal.ONBOARDING_GOAL_SAVING]: 'Saving',
  [OnboardingGoal.ONBOARDING_GOAL_TRANSFERS]: 'Sending money',
  [OnboardingGoal.ONBOARDING_GOAL_NONE]: 'No particular goal',
};

/** What the user came for, from the Nim onboarding chat; unset until they pick. */
export function onboardingGoalLabel(goal: OnboardingGoal | undefined): string {
  return goal ? ONBOARDING_GOAL_LABELS[goal] : 'Not picked yet';
}

// -- How they heard of Liminal (signup question) --

const ACQUISITION_SOURCE_LABELS: Record<AcquisitionSource, string> = {
  [AcquisitionSource.ACQUISITION_SOURCE_FRIEND_OR_FAMILY]: 'Friend or family',
  [AcquisitionSource.ACQUISITION_SOURCE_IN_PERSON]: 'In person',
  [AcquisitionSource.ACQUISITION_SOURCE_APP_STORE]: 'App Store or Google Play',
  [AcquisitionSource.ACQUISITION_SOURCE_GOOGLE_SEARCH]: 'Google search',
  [AcquisitionSource.ACQUISITION_SOURCE_AI_ASSISTANT]: 'AI assistant',
  [AcquisitionSource.ACQUISITION_SOURCE_TIKTOK]: 'TikTok',
  [AcquisitionSource.ACQUISITION_SOURCE_INSTAGRAM]: 'Instagram',
  [AcquisitionSource.ACQUISITION_SOURCE_X]: 'X (Twitter)',
  [AcquisitionSource.ACQUISITION_SOURCE_YOUTUBE]: 'YouTube',
  [AcquisitionSource.ACQUISITION_SOURCE_REDDIT]: 'Reddit',
  [AcquisitionSource.ACQUISITION_SOURCE_PODCAST_OR_NEWS]: 'Podcast, news or blog',
  [AcquisitionSource.ACQUISITION_SOURCE_OTHER]: 'Other',
  [AcquisitionSource.ACQUISITION_SOURCE_SOCIAL_MEDIA]: 'Social media',
};

/** The signup answer, with the user's own words for "Other"; unset for older profiles. */
export function acquisitionSourceLabel(source: AcquisitionSource | undefined, detail: string | undefined): string {
  if (!source) return 'Not answered';
  const label = ACQUISITION_SOURCE_LABELS[source];
  return detail ? `${label}: ${detail}` : label;
}

// -- Onboarding nudges (LIM-3551) --

/** The stages the nudge engine puts a user in (core/notifications/notifications_service/lifecycle). */
export type LifecycleStage = 'verify' | 'fund' | 'first_action' | 'adopt' | 'out_of_loop' | 'excluded';

const LIFECYCLE_STAGE_LABELS: Record<LifecycleStage, string> = {
  verify: 'Verify',
  fund: 'Fund',
  first_action: 'First action',
  adopt: 'Adopt',
  out_of_loop: 'Using Liminal',
  excluded: 'Excluded',
};

/**
 * Stages the user moves through read as info; using two products is the goal
 * reached; excluded (declined, suspended, deleted) is the only warning.
 */
const LIFECYCLE_STAGE_VARIANTS: Record<LifecycleStage, StatusPillTone> = {
  verify: 'info',
  fund: 'info',
  first_action: 'info',
  adopt: 'info',
  out_of_loop: 'success',
  excluded: 'warning',
};

/** What each stage means, for the journey stepper's tooltips. */
const LIFECYCLE_STAGE_DESCRIPTIONS: Record<LifecycleStage, string> = {
  verify: 'Signed up, not verified yet. Unverified users stay here whatever else they have done. Nudges ask them to verify.',
  fund: 'Verified, but no money in yet. Nudges ask them to add money.',
  first_action: 'Money in, but no money action yet (send, save or invest). Nudges suggest a first one, led by their goal.',
  adopt: 'Uses one product. Nudges suggest a second one.',
  out_of_loop: 'Uses two products. The goal is reached, so no stage nudges go out.',
  excluded: 'Declined, suspended, banned or deleted. Never nudged.',
};

export function lifecycleStageDescription(stage: LifecycleStage): string {
  return LIFECYCLE_STAGE_DESCRIPTIONS[stage];
}

/** The path a user walks, in order. Excluded is off the path. */
export const LIFECYCLE_JOURNEY: readonly LifecycleStage[] = ['verify', 'fund', 'first_action', 'adopt', 'out_of_loop'];

/** Where the stage sits on the path; -1 when it is not on it (unseen or excluded). */
export function lifecycleJourneyIndex(stage: string | undefined): number {
  return isLifecycleStage(stage) ? LIFECYCLE_JOURNEY.indexOf(stage) : -1;
}

function isLifecycleStage(stage: string | undefined): stage is LifecycleStage {
  return stage !== undefined && stage in LIFECYCLE_STAGE_LABELS;
}

/** An empty stage means the engine has not seen the user yet. */
export function lifecycleStageLabel(stage: string | undefined): string {
  if (!stage) return 'Not seen yet';
  return isLifecycleStage(stage) ? LIFECYCLE_STAGE_LABELS[stage] : stage;
}

export function lifecycleStageVariant(stage: string | undefined): StatusPillTone {
  return isLifecycleStage(stage) ? LIFECYCLE_STAGE_VARIANTS[stage] : 'neutral';
}

/** A send went out; a skip is worth a look (no channel, or opted out). */
export function lifecycleOutcomeVariant(outcome: string | undefined): StatusPillTone {
  if (outcome === 'sent') return 'success';
  if (outcome === 'skipped') return 'warning';
  return 'neutral';
}

// -- Partner Rewards (LIM-4273) --

// Keyed by every generated member, so a new state fails the typecheck here
// rather than rendering unlabelled.
const MERCHANT_STATES: Record<RewardMerchant_State, { label: string; tone: StatusPillTone }> = {
  [RewardMerchant_State.STATE_ACTIVE]: { label: 'Active', tone: 'success' },
  [RewardMerchant_State.STATE_PAUSED]: { label: 'Paused', tone: 'warning' },
  [RewardMerchant_State.STATE_RETIRED]: { label: 'Retired', tone: 'neutral' },
};

const BUDGET_STATES: Record<RewardBudget_State, { label: string; tone: StatusPillTone }> = {
  [RewardBudget_State.STATE_OPEN]: { label: 'Open', tone: 'success' },
  [RewardBudget_State.STATE_PAUSED]: { label: 'Paused', tone: 'warning' },
  [RewardBudget_State.STATE_CLOSED]: { label: 'Closed', tone: 'neutral' },
};

/** A partner's state as a pill. The server always writes it, so a missing one throws. */
export function merchantState(state: RewardMerchant_State | undefined): { label: string; tone: StatusPillTone } {
  if (state === undefined) throw new Error('reward merchant has no state');
  return MERCHANT_STATES[state];
}

/** A budget's state as a pill. The server always writes it, so a missing one throws. */
export function budgetState(state: RewardBudget_State | undefined): { label: string; tone: StatusPillTone } {
  if (state === undefined) throw new Error('reward budget has no state');
  return BUDGET_STATES[state];
}

// -- The partner catalogue (LIM-4607) --

// The labels customers see in the app, from @liminal/content/rewards. Typed to
// the generated enum, so a category added to the proto fails the type-check
// here until the shared module has a label for it.
const MERCHANT_CATEGORY_LABELS: Readonly<Record<RewardMerchant_Category, string>> = PARTNER_CATEGORY_LABELS;

/**
 * A partner's category as a chip: a category is a kind, not a state, so every
 * chip is neutral. lib/rewards' merchantCategoryOf reads the wire value first.
 */
export function merchantCategory(category: RewardMerchant_Category): { label: string; tone: StatusPillTone } {
  return { label: MERCHANT_CATEGORY_LABELS[category], tone: 'neutral' };
}

// Whether customers see the partner now. Paused and Between windows both
// hide it with the record intact; Paused is the manual switch, so it is the
// one that asks for attention.
const MERCHANT_AVAILABILITY: Record<MerchantAvailability, { label: string; tone: StatusPillTone }> = {
  live: { label: 'Live', tone: 'success' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  between: { label: 'Between windows', tone: 'neutral' },
  paused: { label: 'Paused', tone: 'warning' },
  retired: { label: 'Retired', tone: 'neutral' },
};

/** Where a partner stands for customers, from lib/rewards' merchantAvailability, as a pill. */
export function merchantAvailabilityPill(availability: MerchantAvailability): { label: string; tone: StatusPillTone } {
  return MERCHANT_AVAILABILITY[availability];
}

/** Where an active window is against now, on the partner's detail page. */
export type WindowPhase = 'open' | 'upcoming' | 'ended';

const WINDOW_PHASES: Record<WindowPhase, { label: string; tone: StatusPillTone }> = {
  open: { label: 'Open now', tone: 'success' },
  upcoming: { label: 'Upcoming', tone: 'info' },
  ended: { label: 'Ended', tone: 'neutral' },
};

/** An active window's phase as a pill. */
export function windowPhasePill(phase: WindowPhase): { label: string; tone: StatusPillTone } {
  return WINDOW_PHASES[phase];
}

// Labels name what happened in plain words; the description is the one line
// the payment's details show under its pill.
const PAYMENT_STATES: Record<RewardPayment_State, { label: string; tone: StatusPillTone; description: string }> = {
  [RewardPayment_State.STATE_OPEN]: {
    label: 'Awaiting choice',
    tone: 'info',
    description: 'The customer has not chosen yet: earn LIL, or pay with LIL and get the money back.',
  },
  [RewardPayment_State.STATE_EARNED]: {
    label: 'Earned LIL',
    tone: 'success',
    description: 'The customer chose to earn. The LIL is in their rewards.',
  },
  [RewardPayment_State.STATE_PAYING]: {
    label: 'Paying back',
    tone: 'warning',
    description: 'The customer chose Pay with LIL. Their LIL and the money back are still moving.',
  },
  [RewardPayment_State.STATE_REDEEMED]: {
    label: 'Paid with LIL',
    tone: 'success',
    description: 'The customer paid with LIL and got the money back.',
  },
  [RewardPayment_State.STATE_REFUNDED]: {
    label: 'Refunded',
    tone: 'neutral',
    description: 'The payment was refunded in full before the customer chose.',
  },
  [RewardPayment_State.STATE_REVIEW]: {
    label: 'Needs review',
    tone: 'error',
    description: 'The payment was refunded after the customer chose. A person has to settle it.',
  },
};

// The steps of a Pay with LIL choice: take the customer's LIL, then send the
// money back. Nothing retries a stopped one; a person finishes it.
const REDEMPTION_STATES: Record<RewardRedemption_State, { label: string; tone: StatusPillTone }> = {
  [RewardRedemption_State.STATE_ACCEPTED]: { label: 'Starting', tone: 'info' },
  [RewardRedemption_State.STATE_COLLECTING]: { label: 'Taking LIL', tone: 'warning' },
  [RewardRedemption_State.STATE_COLLECTED]: { label: 'Money owed', tone: 'warning' },
  [RewardRedemption_State.STATE_COLLECT_FAILED]: { label: 'LIL not taken', tone: 'neutral' },
  [RewardRedemption_State.STATE_REIMBURSING]: { label: 'Sending money', tone: 'warning' },
  [RewardRedemption_State.STATE_REDEEMED]: { label: 'Done', tone: 'success' },
};

/** A reward payment's state as a pill. The server always writes it, so a missing one throws. */
export function paymentState(
  state: RewardPayment_State | undefined,
): { label: string; tone: StatusPillTone; description: string } {
  if (state === undefined) throw new Error('reward payment has no state');
  return PAYMENT_STATES[state];
}

/** A redemption's state as a pill. The server always writes it, so a missing one throws. */
export function redemptionState(state: RewardRedemption_State | undefined): { label: string; tone: StatusPillTone } {
  if (state === undefined) throw new Error('reward redemption has no state');
  return REDEMPTION_STATES[state];
}

// -- Nim calls (LIM-4589) --

const NIM_CALL_STATES: Record<NimCall_State, { label: string; tone: StatusPillTone }> = {
  [NimCall_State.STATE_RINGING]: { label: 'Ringing', tone: 'info' },
  [NimCall_State.STATE_ANSWERED]: { label: 'Live', tone: 'info' },
  [NimCall_State.STATE_DECLINED]: { label: 'Declined', tone: 'neutral' },
  [NimCall_State.STATE_MISSED]: { label: 'Missed', tone: 'warning' },
  [NimCall_State.STATE_ENDED]: { label: 'Ended', tone: 'success' },
};

/** A call's state as a pill. The server always writes it, so a missing or unknown one throws. */
export function nimCallState(state: NimCall_State | undefined): { label: string; tone: StatusPillTone } {
  if (state === undefined) throw new Error('Nim call has no state');
  const pill = NIM_CALL_STATES[state];
  if (pill === undefined) throw new Error(`Nim call has an unknown state ${state}`);
  return pill;
}

const NIM_CALL_INITIATORS: Record<NimCallInitiator, string> = {
  [NimCallInitiator.NIM_CALL_INITIATOR_USER]: 'The user',
  [NimCallInitiator.NIM_CALL_INITIATOR_AGENT]: 'Nim, in a chat',
  [NimCallInitiator.NIM_CALL_INITIATOR_SCHEDULED]: 'A scheduled task',
  [NimCallInitiator.NIM_CALL_INITIATOR_ADMIN]: 'An operator',
};

/** Who started or placed a call. The server always writes it, so a missing or unknown one throws. */
export function nimCallInitiatorLabel(initiator: NimCallInitiator | undefined): string {
  if (initiator === undefined) throw new Error('Nim call has no initiator');
  const label = NIM_CALL_INITIATORS[initiator];
  if (label === undefined) throw new Error(`Nim call has an unknown initiator ${initiator}`);
  return label;
}

// Why a call ended or stopped ringing, as the operator reads it.
const NIM_CALL_END_REASONS: Record<NimCallEndReason, string> = {
  [NimCallEndReason.NIM_CALL_END_REASON_END_CALL]: 'User ended it in the app',
  [NimCallEndReason.NIM_CALL_END_REASON_CALLER_NEVER_JOINED]: "User's phone never joined",
  [NimCallEndReason.NIM_CALL_END_REASON_CALLER_LEFT]: "User's phone left",
  [NimCallEndReason.NIM_CALL_END_REASON_CALLER_ENDED]: 'User hung up',
  [NimCallEndReason.NIM_CALL_END_REASON_NIM_DISCONNECTED]: 'Nim lost the call',
  [NimCallEndReason.NIM_CALL_END_REASON_AUDIO_FAILED]: 'Audio failed',
  [NimCallEndReason.NIM_CALL_END_REASON_ASSISTANT_ENDED]: 'Nim said goodbye',
  [NimCallEndReason.NIM_CALL_END_REASON_VOICE_FAILED]: "Nim's voice failed",
  [NimCallEndReason.NIM_CALL_END_REASON_MAX_LENGTH]: 'Reached the maximum length',
  [NimCallEndReason.NIM_CALL_END_REASON_SHUTDOWN]: 'Voice service shut down',
  [NimCallEndReason.NIM_CALL_END_REASON_DECLINED]: 'User declined',
  [NimCallEndReason.NIM_CALL_END_REASON_RING_TIMEOUT]: 'Not answered in time',
  [NimCallEndReason.NIM_CALL_END_REASON_NO_DEVICE_REACHED]: 'No phone could be rung',
  [NimCallEndReason.NIM_CALL_END_REASON_LOST]: 'Record closed after it was lost',
};

/**
 * Why a call that has ended (or stopped ringing) did. Only asked of a call
 * with an end time: one that rings or is live carries the zero value, which
 * the generated enum leaves out, so a reason missing from the map throws.
 */
export function nimCallEndReasonLabel(reason: NimCallEndReason | undefined): string {
  if (reason === undefined) throw new Error('Nim call ended with no end reason');
  const label = NIM_CALL_END_REASONS[reason];
  if (label === undefined) throw new Error(`Nim call ended with an unknown end reason ${reason}`);
  return label;
}

// -- Nim conversation flags --

// Open is waiting on someone, so it asks for attention; actioned is done.
const CONVERSATION_FLAG_STATES: Record<ConversationFlag_State, { label: string; tone: StatusPillTone }> = {
  [ConversationFlag_State.STATE_OPEN]: { label: 'Open', tone: 'warning' },
  [ConversationFlag_State.STATE_REVIEWED]: { label: 'Reviewed', tone: 'info' },
  [ConversationFlag_State.STATE_ACTIONED]: { label: 'Actioned', tone: 'success' },
};

/** A flag's review state as a pill. An unknown one throws rather than passing for another. */
export function conversationFlagState(state: ConversationFlag_State): { label: string; tone: StatusPillTone } {
  const pill = CONVERSATION_FLAG_STATES[state];
  if (pill === undefined) throw new Error(`Conversation flag has an unknown state ${state}`);
  return pill;
}

const CONVERSATION_FLAG_SOURCES: Record<ConversationFlag_Source, string> = {
  [ConversationFlag_Source.SOURCE_ADMIN]: 'Admin',
  [ConversationFlag_Source.SOURCE_SLACK]: 'Slack',
};

/** Where a flag was raised. An unknown source throws. */
export function conversationFlagSourceLabel(source: ConversationFlag_Source): string {
  const label = CONVERSATION_FLAG_SOURCES[source];
  if (label === undefined) throw new Error(`Conversation flag has an unknown source ${source}`);
  return label;
}

// -- Event check-in (LIM-4707) --

// Whether a guest at the door may be handed tickets, in the words staff use.
// Submitted and Verified both may: submitted is a guest who did what was
// asked, waiting on a decision, so it is information, not a warning.
const EVENT_GUEST_ELIGIBILITIES: Record<EventGuest_Eligibility, { label: string; tone: StatusPillTone }> = {
  [EventGuest_Eligibility.ELIGIBILITY_SUBMITTED]: { label: 'Submitted', tone: 'info' },
  [EventGuest_Eligibility.ELIGIBILITY_VERIFIED]: { label: 'Verified', tone: 'success' },
  [EventGuest_Eligibility.ELIGIBILITY_NOT_SUBMITTED]: { label: 'Not submitted', tone: 'warning' },
  [EventGuest_Eligibility.ELIGIBILITY_REJECTED]: { label: 'Rejected', tone: 'error' },
};

/** A guest's eligibility as a pill. An unknown one throws rather than passing for another. */
export function eventGuestEligibility(eligibility: EventGuest_Eligibility): { label: string; tone: StatusPillTone } {
  const pill = EVENT_GUEST_ELIGIBILITIES[eligibility];
  if (pill === undefined) throw new Error(`Event guest has an unknown eligibility ${eligibility}`);
  return pill;
}

// -- Referral attribution (LIM-4840) --

// How a referral was attributed to its inviter, named as the referral.redeemed
// event's source property names it in PostHog, so the two read alike.
const ATTRIBUTION_SOURCES: Record<AttributionSource, string> = {
  [AttributionSource.ATTRIBUTION_SOURCE_EMAIL_BIND]: 'Email bind',
  [AttributionSource.ATTRIBUTION_SOURCE_INSTALL_REFERRER]: 'Install referrer',
  [AttributionSource.ATTRIBUTION_SOURCE_INSTALL_CLICK]: 'Install click',
  [AttributionSource.ATTRIBUTION_SOURCE_APP_CLIP]: 'App Clip',
  [AttributionSource.ATTRIBUTION_SOURCE_MANUAL_ENTRY]: 'Manual entry',
};

/** How a referral was attributed. The server always writes it, so a missing or unknown one throws. */
export function attributionSourceLabel(source: AttributionSource | undefined): string {
  if (source === undefined) throw new Error('Referral has no attribution source');
  const label = ATTRIBUTION_SOURCES[source];
  if (label === undefined) throw new Error(`Referral has an unknown attribution source ${source}`);
  return label;
}
